import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatOvaryAdnexa } from './pdfHelpers';
import { PdfAccentSectionHeader, PdfLabelValue } from './PdfSharedComponents';

function formatMaternalMass(mass) {
  const parts = [];
  if (mass.echotexture) parts.push(mass.echotexture);
  if (mass.locationType) parts.push(mass.locationType);
  if (mass.location) parts.push(`at ${mass.location}`);
  const size = [mass.size1, mass.size2, mass.size3].filter(Boolean).join(' x ');
  if (size) parts.push(`(${size} cm)`);
  if (mass.description) parts.push(`— ${mass.description}`);
  return parts.join(' ');
}

function getOvaryReport(label, ovary = {}) {
  const masses = (ovary.masses || []).filter(Boolean);
  const statusList = Array.isArray(ovary.status) ? ovary.status : [ovary.status].filter(Boolean);
  // A described mass overrides a status left on "Normal" — the report must
  // never call the ovary normal while listing a mass
  const status = masses.length > 0 && statusList.join(', ') === 'Normal' ? 'See masses' : statusList.join(', ');
  const adnexa = formatOvaryAdnexa(label, status, ovary.length, ovary.width, ovary.depth);
  return {
    label,
    masses,
    other: ovary.other,
    text: adnexa.adnexaText,
    measurementLine: adnexa.measurementLine,
    isAbnormal: (!!status && status !== 'Normal') || adnexa.sizeAbnormal
  };
}

function OvaryRows({ report }) {
  return (
    <>
      <PdfLabelValue label={`${report.label}:`} value={report.text} isAbnormal={report.isAbnormal} />
      {report.other ? <PdfLabelValue label={`${report.label} findings:`} value={report.other} isAbnormal /> : null}
      {report.masses.length > 0 ? (
        <PdfLabelValue label={`${report.label} masses:`} value={report.masses.map(formatMaternalMass).join('\n')} isAbnormal />
      ) : null}
    </>
  );
}

// The scanning approach chosen in the form's Exam Type section
export function PdfExamDetails({ examTypeDetails = {} }) {
  const approach = examTypeDetails.transvaginalOnly ? 'Transvaginal Only'
    : examTypeDetails.transvaginal ? 'Transabdominal & Transvaginal'
    : examTypeDetails.transabdominal ? 'Transabdominal Only'
    : null;
  if (!approach) return null;

  return (
    <>
      <PdfAccentSectionHeader title="EXAM DETAILS" />
      <PdfLabelValue label="Approach:" value={approach} />
    </>
  );
}

// Left-column box listing any enlarged ovary, only when there is one
export function PdfMaternalMeasurements({ maternal }) {
  const lines = [
    getOvaryReport('Right ovary', maternal.rightOvary).measurementLine,
    getOvaryReport('Left ovary', maternal.leftOvary).measurementLine
  ].filter(Boolean);
  if (lines.length === 0) return null;

  return (
    <View style={commonStyles.measurementBox}>
      <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>Maternal</Text>
      {lines.map(line => (
        <Text key={line} style={[commonStyles.infoBoxText, { color: COLORS.abnormal, fontFamily: 'Helvetica-Bold' }]}>{line}</Text>
      ))}
    </View>
  );
}

// Uterus / Endometrium (with cervix) / Adnexa — the same right-column sections on every OB report.
// Later-pregnancy worksheets have no endometrium fields, so their cervix findings head their own section.
export function PdfMaternalSections({ maternal }) {
  const endo = maternal.endometrium || {};
  const culDeSac = maternal.culDeSac || {};
  const uterusMasses = (maternal.uterusMasses || []).filter(Boolean);
  // A described mass overrides a uterus left on "Normal" — the report must
  // never call the uterus normal while listing a mass
  const uterusText = uterusMasses.length > 0 && (!maternal.uterus || maternal.uterus === 'Normal') ? 'See uterine masses' : (maternal.uterus || '');
  const hasEndometriumSection = !!(endo.thickness || endo.retainedProducts || endo.otherFindings ||
    maternal.cervixLength || maternal.cervixNotVisualized || maternal.cervixFunneling);

  return (
    <>
      {/* wrap={false} keeps a section's heading with its findings across a page break */}
      <View wrap={false}>
        <PdfAccentSectionHeader title="UTERUS" />
        <PdfLabelValue label="Uterus:" value={uterusText} isAbnormal={!!uterusText && uterusText !== 'Normal'} />
        {maternal.priorCSection ? <PdfLabelValue label="Prior C-section:" value="Yes" /> : null}
        {maternal.priorCSection && maternal.placentaScarDistance ? <PdfLabelValue label="Placenta to scar distance:" value={`${maternal.placentaScarDistance} cm`} /> : null}
        {uterusMasses.length > 0 ? (
          <PdfLabelValue label="Uterine masses:" value={uterusMasses.map(formatMaternalMass).join('\n')} isAbnormal />
        ) : null}
      </View>

      {hasEndometriumSection ? (
        <View wrap={false}>
          <PdfAccentSectionHeader title={maternal.endometrium ? 'ENDOMETRIUM' : 'CERVIX'} />
          {endo.thickness ? <PdfLabelValue label="Thickness:" value={`${endo.thickness} mm`} /> : null}
          {endo.retainedProducts ? <PdfLabelValue label="Retained products:" value="Yes" isAbnormal /> : null}
          {endo.otherFindings ? <PdfLabelValue label="Additional findings:" value={endo.otherFindings} isAbnormal /> : null}
          {maternal.cervixNotVisualized ? (
            <PdfLabelValue label="Cervix:" value="Not well visualized" isAbnormal />
          ) : maternal.cervixLength ? <PdfLabelValue label="Cervix:" value={`${maternal.cervixLength} cm`} /> : null}
          {maternal.cervixFunneling ? <PdfLabelValue label="Cervical funneling:" value="Present" isAbnormal /> : null}
        </View>
      ) : null}

      <View wrap={false}>
        <PdfAccentSectionHeader title="ADNEXA" />
        <OvaryRows report={getOvaryReport('Right ovary', maternal.rightOvary)} />
        <OvaryRows report={getOvaryReport('Left ovary', maternal.leftOvary)} />
        <PdfLabelValue label="Cul-de-sac fluid:" value={culDeSac.fluidVolume || 'No free fluid'} isAbnormal={!!culDeSac.fluidVolume && culDeSac.fluidVolume !== 'No free fluid'} />
        {culDeSac.other ? <PdfLabelValue label="Cul-de-sac findings:" value={culDeSac.other} isAbnormal /> : null}
      </View>
    </>
  );
}
